import { useCallback, useRef, useState } from "react";
import { useToast } from "../../components/ui/toast";
import { OutcomeBlockModal } from "./outcome-findings";
import {
  oldFormatFields,
  splitFindings,
  validateOutcomes,
  type OutcomeFinding,
  type OutcomeOverride,
  type OutcomeValidation,
  type ValidateRequest,
} from "./outcome-validation";

interface CheckOptions {
  // New agents and workflows are only advised: core logs them and never blocks, so nothing here says "must fix".
  advisoryOnly?: boolean;
}

// One place that runs the outcome check for a studio: the live check while editing, the check at
// publish time, the "needs changes" dialog, and the retry with the author's reasons.
// Behaviour follows core's mode: "off" shows nothing, "shadow" only advises, "enforce" can stop a publish.
export function useOutcomeGate() {
  const toast = useToast();
  const [validation, setValidation] = useState<OutcomeValidation | null>(null);
  const [block, setBlock] = useState<{ findings: OutcomeFinding[]; retry: (overrides: OutcomeOverride[]) => void } | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const latest = useRef(0);

  const check = useCallback(async (request: ValidateRequest, options: CheckOptions = {}) => {
    const id = ++latest.current;
    let result = await validateOutcomes(request);
    if (result && options.advisoryOnly) {
      result = { ...result, can_save: true, findings: result.findings.map((finding) => ({ ...finding, blocking: false })) };
    }
    if (id === latest.current) setValidation(result);
    return result;
  }, []);

  const reviewWithAi = useCallback(async (request: ValidateRequest, options: CheckOptions = {}) => {
    setReviewing(true);
    try {
      return await check({ ...request, llm_review: true }, options);
    } finally {
      setReviewing(false);
    }
  }, [check]);

  const openBlock = useCallback((findings: OutcomeFinding[], retry: (overrides: OutcomeOverride[]) => void) => {
    setBlock({ findings, retry });
  }, []);

  // Called when the author clicks Publish. Returns true when the save was stopped and the dialog is open.
  const stopsPublish = useCallback(async (
    request: ValidateRequest,
    retry: (overrides: OutcomeOverride[]) => void,
    options: CheckOptions = {},
  ): Promise<boolean> => {
    const result = await check(request, options);
    if (!result || result.mode === "off") return false;
    const { blocking, suggestions } = splitFindings(result.findings);
    if (result.mode === "enforce" && !options.advisoryOnly && blocking.length) {
      setBlock({ findings: result.findings, retry });
      return true;
    }
    const oldFormat = oldFormatFields(result.findings).length;
    if (suggestions.length || oldFormat) {
      toast.info(
        "Outcome suggestions",
        [
          suggestions.length ? `${suggestions.length} suggestion${suggestions.length === 1 ? "" : "s"}` : "",
          oldFormat ? `${oldFormat} outcome${oldFormat === 1 ? " uses" : "s use"} the old text format; you can switch to four boxes` : "",
        ].filter(Boolean).join(" · "),
      );
    }
    return false;
  }, [check, toast]);

  const modal = block ? (
    <OutcomeBlockModal
      findings={block.findings}
      onClose={() => setBlock(null)}
      onContinue={(overrides) => {
        const retry = block.retry;
        setBlock(null);
        retry(overrides);
      }}
    />
  ) : null;

  return { validation, check, reviewWithAi, reviewing, stopsPublish, openBlock, modal, showOld, setShowOld };
}
